'use client';

import { File01Icon, Image01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ConversationAttachmentReference } from '@terra/shared-dto-chat-types';
import { formatFileSize } from '../../documents-reports/utils';
import { ContextPill } from './ContextPill';

export type AttachmentContextPillModel = {
  id: string;
  fileName: string;
  fileType: ConversationAttachmentReference['fileType'];
  sizeBytes?: number;
  previewUrl?: string;
  progress?: number;
  status?: 'uploading' | 'error';
  error?: string;
};

type AttachmentContextPillProps = {
  attachment: AttachmentContextPillModel;
  onRemove?: () => void;
  onPreview?: () => void;
  removeAriaLabel: string;
};

export function AttachmentContextPill({
  attachment,
  onRemove,
  onPreview,
  removeAriaLabel,
}: AttachmentContextPillProps) {
  const t = useTranslations('designSystem.chat.fileUpload');
  const isImage = attachment.fileType === 'IMAGE';
  const status = attachment.status ?? 'default';
  const sizeLabel =
    typeof attachment.sizeBytes === 'number'
      ? formatFileSize(attachment.sizeBytes)
      : undefined;
  const detail =
    status === 'error'
      ? [sizeLabel, attachment.error || t('uploadError')]
          .filter(Boolean)
          .join(' · ')
      : status === 'uploading'
        ? [sizeLabel, `${attachment.progress ?? 0}%`]
            .filter(Boolean)
            .join(' · ')
        : undefined;
  const preview =
    isImage && attachment.previewUrl ? (
      <img
        src={attachment.previewUrl}
        alt={attachment.fileName}
        style={{
          maxWidth: 240,
          maxHeight: 160,
          objectFit: 'contain',
          display: 'block',
          borderRadius: 6,
        }}
      />
    ) : undefined;

  return (
    <ContextPill
      label={attachment.fileName}
      icon={isImage && !attachment.previewUrl ? Image01Icon : File01Icon}
      thumbnailUrl={isImage ? attachment.previewUrl : undefined}
      status={status === 'default' ? 'default' : status}
      detail={detail}
      preview={preview}
      removeAriaLabel={removeAriaLabel}
      onRemove={
        onRemove
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }
          : undefined
      }
      onClick={
        onPreview
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onPreview();
            }
          : undefined
      }
    />
  );
}
